import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { isEqual } from "lodash";

import { type Level } from "@features/overview-v2/types";
import {
  FloatingDelayGroup,
  FloatingPortal,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  type Placement,
} from "@floating-ui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { usePrevious } from "@hooks/use-previous";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { useVirtualizer } from "@tanstack/react-virtual";

import { type TopLevelBreakdown } from "../../types";
import { breakdownComparator } from "../../utils/breakdown-comparator";

import { DefaultButtonContent } from "./default-button-content";
import { Option } from "./option";
import { OptionWithSubmenu } from "./option-with-submenu";

interface Props {
  mode: "breakdowns" | "breakdowns-and-values";
  showRemoveButton?: boolean;
  showResetButton?: boolean;
  closeOnReset?: boolean;
  text?: string;
  breakdownConfigs: CategoricalBreakdownConfig[];
  selectedBreakdowns: TopLevelBreakdown[];
  buttonContentComponent?(props: { isOpen: boolean }): ReactNode;
  buttonClassName?: string;
  buttonActiveClassName?: string;
  butttonTextClassName?: string;
  butttonActiveTextClassName?: string;
  menuPlacement?: Placement;
  labelsByLevel?: Record<string, Level>;
  disabled?: boolean;
  onSelectionChange(values: TopLevelBreakdown[]): void;
  onDelete?(value: TopLevelBreakdown): void;
}

export const BreakdownSelector = ({
  mode,
  showRemoveButton,
  showResetButton,
  closeOnReset,
  text,
  breakdownConfigs,
  selectedBreakdowns,
  buttonContentComponent,
  buttonClassName,
  buttonActiveClassName,
  butttonTextClassName,
  butttonActiveTextClassName,
  menuPlacement = "bottom-end",
  labelsByLevel,
  disabled,
  onSelectionChange,
  onDelete,
}: Props) => {
  const scrollElRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const prevOpen = usePrevious(isOpen);
  const [isHovering, setIsHovering] = useState(false);

  const [bufferedBreakdowns, setBufferedBreakdowns] =
    useState(selectedBreakdowns);

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    breakdownConfigs,
    {
      searchKeys: ["name"],
    },
  );

  const changeMenuOpen = useCallback(
    (value: boolean) => {
      if (value) {
        clearSearch();
        setBufferedBreakdowns(selectedBreakdowns);
      }
      setIsOpen(value);
    },
    [clearSearch, selectedBreakdowns],
  );

  const {
    refs,
    floatingStyles,
    context,
    update: updateFloating,
  } = useFloating({
    open: isOpen,
    onOpenChange: changeMenuOpen,

    transform: false,
    placement: menuPlacement,
    middleware: [flip(), offset(4)],
  });

  useEffect(() => {
    //  Doing this specifically because we want fine grain control over the firings of the selection change
    if (
      mode === "breakdowns-and-values" &&
      // we only want to fire when the previous state was open but the new state is closed
      prevOpen &&
      !isOpen &&
      !isEqual(bufferedBreakdowns, selectedBreakdowns)
    ) {
      onSelectionChange(bufferedBreakdowns);
    }
  }, [
    isOpen,
    bufferedBreakdowns,
    selectedBreakdowns,
    mode,
    onSelectionChange,
    prevOpen,
  ]);

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => updateFloating(), [search, updateFloating]);

  const sortedOptions = useMemo(
    () =>
      [...filteredOptions].sort((a, b) => breakdownComparator(a.name, b.name)),
    [filteredOptions],
  );

  const rowVirtualizer = useVirtualizer({
    count: sortedOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  const shouldShowApply = mode === "breakdowns-and-values";

  return (
    <div>
      <button
        disabled={disabled}
        className={clsx(
          !isOpen &&
            (buttonClassName
              ? buttonClassName
              : "h-full px-3 flex items-center"),
          isOpen ? buttonActiveClassName : null,
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {buttonContentComponent ? (
          buttonContentComponent({ isOpen: isOpen })
        ) : (
          <DefaultButtonContent
            text={text}
            isHovering={isHovering}
            isOpen={isOpen}
            butttonActiveTextClassName={butttonActiveTextClassName}
            butttonTextClassName={butttonTextClassName}
          />
        )}
      </button>
      {isOpen && (
        <FloatingPortal>
          <AnimatePresence>
            (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              ref={refs.setFloating}
              style={floatingStyles}
              className={clsx(
                "absolute z-[2001] bg-white rounded-lg elevation-1 pt-3 pb-2 w-[250px]",
                "text-sm font-sans",
              )}
              {...getFloatingProps()}
            >
              <div className="nb-input-container relative px-4 mb-2">
                <input
                  className="nb-input pr-8"
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
                    "w-4 absolute right-[24px] top-[10px]",
                  )}
                />
              </div>
              {sortedOptions.length === 0 && (
                <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
              )}
              <div
                ref={scrollElRef}
                className="max-h-[300px] overflow-y-auto floatingui-scroll"
              >
                <div
                  style={{
                    position: "relative",
                    height: `${rowVirtualizer.getTotalSize()}px`,
                  }}
                >
                  <FloatingDelayGroup delay={{ open: 0, close: 2000 }}>
                    {rowVirtualizer.getVirtualItems().map((e) => {
                      const row = sortedOptions[e.index];

                      return mode === "breakdowns" ? (
                        <Option
                          key={e.key}
                          style={{ transform: `translateY(${e.start}px)` }}
                          className={clsx({
                            "font-bold": selectedBreakdowns
                              .map((e) => e.key)
                              .includes(row.name),
                          })}
                          row={row}
                          onClick={() => {
                            onSelectionChange([
                              {
                                key: row.key,
                                values: row.choices.map((e) => e.value),
                              },
                            ]);

                            changeMenuOpen(false);
                          }}
                        />
                      ) : (
                        <OptionWithSubmenu
                          key={e.key}
                          style={{ transform: `translateY(${e.start}px)` }}
                          row={row}
                          selectedValues={
                            bufferedBreakdowns.find((v) => v.key === row.key)
                              ?.values || []
                          }
                          labelsByLevel={labelsByLevel}
                          onChange={(values) => {
                            if (values.length === 0) {
                              setBufferedBreakdowns(
                                bufferedBreakdowns.filter(
                                  (v) => v.key !== row.key,
                                ),
                              );
                            } else {
                              if (
                                bufferedBreakdowns
                                  .map((v) => v.key)
                                  .includes(row.key)
                              ) {
                                setBufferedBreakdowns(
                                  bufferedBreakdowns.map((v) =>
                                    v.key === row.key
                                      ? { key: v.key, values }
                                      : v,
                                  ),
                                );
                              } else {
                                setBufferedBreakdowns([
                                  ...bufferedBreakdowns,
                                  { key: row.key, values: [...values] },
                                ]);
                              }
                            }
                          }}
                        />
                      );
                    })}
                  </FloatingDelayGroup>
                </div>
              </div>
              {(shouldShowApply || showRemoveButton || showResetButton) && (
                <div className="border-t order-gray-1 px-4 pt-2 pb-0.5 flex justify-center space-x-1">
                  {showResetButton && (
                    <PrimaryButton
                      className="bg-mars-2 text-mars font-bold hover:bg-mars-3 flex-grow"
                      onClick={() => {
                        onDelete &&
                          selectedBreakdowns[0] &&
                          onDelete(selectedBreakdowns[0]);

                        if (closeOnReset) {
                          setIsOpen(false);
                        }
                      }}
                    >
                      CLEAR
                    </PrimaryButton>
                  )}
                  {shouldShowApply && (
                    <PrimaryButton
                      className={clsx(
                        "hover:bg-primary hover:text-white font-bold",
                        "bg-cosmic-3 text-primary flex-grow",
                      )}
                      onClick={() => changeMenuOpen(false)}
                    >
                      APPLY
                    </PrimaryButton>
                  )}
                  {showRemoveButton && (
                    <PrimaryButton
                      className="bg-mars-2 text-mars font-bold hover:bg-mars-3 flex-grow"
                      onClick={() =>
                        onDelete &&
                        selectedBreakdowns[0] &&
                        onDelete(selectedBreakdowns[0])
                      }
                    >
                      REMOVE
                    </PrimaryButton>
                  )}
                </div>
              )}
            </motion.div>
            )
          </AnimatePresence>
        </FloatingPortal>
      )}
    </div>
  );
};
